// Electrical Certificate Form Configs (simpler certs using generic form engine)

export const ELEC_FORMS = {
  "solar-pv": {
    title: "Solar PV Electrical Certificate",
    type: "solar_pv_elec",
    accent: "gold",
    sections: [
      { title: "PV System Details", fields: [
        { id: "module_make", label: "PV module make & model", type: "text" },
        { id: "module_count", label: "Number of modules", type: "number" },
        { id: "module_wp", label: "Module rated power (Wp)", type: "number" },
        { id: "total_kwp", label: "Total array capacity (kWp)", type: "number" },
        { id: "inverter_make", label: "Inverter make & model", type: "text" },
        { id: "inverter_kw", label: "Inverter rated power (kW)", type: "number" },
        { id: "orientation", label: "Array orientation", type: "text", placeholder: "e.g. South" },
        { id: "tilt", label: "Array tilt (degrees)", type: "number" },
        { id: "mounting", label: "Mounting type", type: "select", options: ["Pitched roof", "In-roof", "Flat roof", "Ground mount", "Wall mount"] },
      ]},
      { title: "Supply & Earthing", fields: [
        { id: "supply_system", label: "Earthing system", type: "select", options: ["TN-S", "TN-C-S", "TT"] },
        { id: "supply_conductors", label: "Live conductors", type: "select", options: ["1-phase (2 wire)", "3-phase (4 wire)"] },
        { id: "nominal_voltage", label: "Nominal voltage (V)", type: "text", placeholder: "230" },
        { id: "external_ze", label: "External Ze (Ω)", type: "text" },
        { id: "pfc", label: "Prospective fault current (kA)", type: "text" },
        { id: "measured_ze", label: "Measured Ze (Ω)", type: "text" },
      ]},
      { title: "DC Test Results", fields: [
        { id: "voc_string1", label: "String 1 Voc (V)", type: "text" },
        { id: "isc_string1", label: "String 1 Isc (A)", type: "text" },
        { id: "voc_string2", label: "String 2 Voc (V)", type: "text" },
        { id: "isc_string2", label: "String 2 Isc (A)", type: "text" },
        { id: "ir_dc_pos", label: "IR DC+ to Earth (MΩ)", type: "text" },
        { id: "ir_dc_neg", label: "IR DC- to Earth (MΩ)", type: "text" },
        { id: "earth_continuity", label: "Earth continuity (Ω)", type: "text" },
        { id: "irradiance", label: "Irradiance at test (W/m²)", type: "text" },
      ]},
      { title: "Schedule of Inspections", fields: [
        { id: "insp_1", label: "1.0 Consumer's intake equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_3", label: "3.0 Protective measure: ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_4", label: "4.0 Basic protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_6", label: "6.0 Additional protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_7", label: "7.0 Distribution equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_8", label: "8.0 Circuits", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_9", label: "9.0 Isolation and switching", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_11", label: "11.0 Identification and notices", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_14", label: "14.0 Prosumer's LV installation", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
      ]},
      { title: "AC Circuit Details", hasDynamicRows: true, rowFields: [
        { id: "ref", label: "Ref" },
        { id: "designation", label: "Designation" },
        { id: "csa_live", label: "Live mm²" },
        { id: "ocpd_type", label: "OCPD type" },
        { id: "ocpd_rating", label: "Rating A" },
        { id: "rcd_type", label: "RCD type" },
        { id: "rcd_idn", label: "IΔn mA" },
        { id: "r1r2", label: "R1+R2 Ω" },
        { id: "zs", label: "Zs Ω" },
        { id: "ir_le", label: "IR L-E MΩ" },
        { id: "polarity", label: "Pol", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "rcd_time", label: "RCD ms" },
      ]},
      { title: "Comments", fields: [
        { id: "departures", label: "Departures from BS 7671", type: "textarea", placeholder: "N/A" },
        { id: "comments", label: "Comments on existing installation", type: "textarea", placeholder: "N/A" },
        { id: "g98_compliant", label: "G98/G99 settings verified", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "dno_notified", label: "DNO notification submitted", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "next_inspection_interval", label: "Next inspection interval", type: "text", placeholder: "e.g. 5 years" },
      ]},
    ],
  },

  "earthing-bonding": {
    title: "Earthing & Bonding Certificate",
    type: "earthing_bonding",
    accent: "gold",
    sections: [
      { title: "Details of Installation", fields: [
        { id: "description_of_works", label: "Description of earthing/bonding work completed", type: "textarea" },
      ]},
      { title: "Details of the Installation", fields: [
        { id: "system_type", label: "System type", type: "select", options: ["TN-S", "TN-C-S", "TT", "TN-C-S (PNB)"] },
        { id: "ze_zdb", label: "Ze/Zdb (Ω)", type: "text" },
        { id: "method_of_protection", label: "Method of protection", type: "text" },
        { id: "bath_shower_bonded", label: "If locations with bath/shower not bonded, are conditions of Reg 701.415.2 met?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "clamps_bs951", label: "All connections made using clamps to BS 951 and protected against corrosion?", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "Earthing/Bonding Connections", hasDynamicRows: true, rowFields: [
        { id: "type", label: "Type", type: "text", placeholder: "e.g. Main earth, Supp. bond" },
        { id: "location", label: "Location", type: "text" },
        { id: "csa", label: "Csa (mm²)", type: "text" },
        { id: "material", label: "Material", type: "select", options: ["Copper", "Aluminium", "Steel", "Other"] },
        { id: "continuity", label: "Continuity (Ω)", type: "text" },
        { id: "compliant", label: "BS 7671 Compliant?", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
    ],
  },

  "danger-notice": {
    title: "Electrical Danger Notice",
    type: "danger_notice",
    accent: "red",
    sections: [
      { title: "Details of the Dangerous Condition", fields: [
        { id: "dangerous_condition", label: "Dangerous condition details", type: "textarea" },
        { id: "risk_of_injury", label: "Risk of injury or loss from", type: "textarea" },
      ]},
      { title: "Organisation / Person Responsible", fields: [
        { id: "responsible_name", label: "Name and address of responsible person/organisation", type: "textarea" },
        { id: "specific_location", label: "Specific location", type: "text" },
      ]},
      { title: "Action Taken", fields: [
        { id: "action_taken", label: "Action taken", type: "textarea" },
      ]},
      { title: "Further Action Required", fields: [
        { id: "further_action", label: "Further action required", type: "textarea" },
      ]},
    ],
  },

  "visual-inspection": {
    title: "Visual Inspection Report",
    type: "visual_inspection",
    accent: "gold",
    sections: [
      { title: "Summary of Condition", fields: [
        { id: "general_condition", label: "General condition of the installation", type: "textarea" },
        { id: "approx_age", label: "Approximate age of installation (years)", type: "number" },
        { id: "evidence_alterations", label: "Evidence of additions or alterations", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "next_inspection", label: "Next inspection due", type: "text", placeholder: "e.g. 5 years" },
        { id: "inspection_type", label: "Recommended inspection type", type: "select", options: ["N/A", "EICR", "Visual Report"] },
      ]},
      { title: "Declaration", fields: [
        { id: "condition_assessment", label: "Condition assessment", type: "select", options: ["N/A", "Visually Satisfactory", "Unsatisfactory — significant damage/deterioration/defects evident"] },
      ]},
      { title: "Observations", hasDynamicRows: true, rowFields: [
        { id: "item_no", label: "Item No.", type: "text" },
        { id: "observation", label: "Observation", type: "text" },
        { id: "code", label: "Code", type: "select", options: ["C1", "C2", "C3", "FI"] },
      ]},
    ],
  },

  "ev-charger": {
    title: "EV Charger Installation Certificate",
    type: "ev_charger",
    accent: "gold",
    sections: [
      { title: "O-PEN Device (if applicable)", fields: [
        { id: "open_manufacturer", label: "O-PEN manufacturer", type: "text" },
        { id: "open_model", label: "O-PEN model", type: "text" },
        { id: "open_serial", label: "O-PEN serial number", type: "text" },
        { id: "open_location", label: "O-PEN location", type: "text" },
      ]},
      { title: "Charging Equipment Details", fields: [
        { id: "ev_manufacturer", label: "Charger manufacturer", type: "text" },
        { id: "ev_model", label: "Charger model", type: "text" },
        { id: "ev_serial", label: "Serial number", type: "text" },
        { id: "ev_charging_mode", label: "Charging mode", type: "select", options: ["Mode 1", "Mode 2", "Mode 3", "Mode 4"] },
        { id: "ev_output_kw", label: "Output (kW)", type: "number" },
        { id: "ev_max_current", label: "Max rated current (A)", type: "number" },
      ]},
      { title: "Supply & Earthing", fields: [
        { id: "supply_system", label: "Supply system", type: "select", options: ["TN-S", "TN-C-S", "TT"] },
        { id: "supply_conductors", label: "Live conductors", type: "select", options: ["1-phase (2 wire)", "3-phase (4 wire)"] },
        { id: "nominal_voltage", label: "Nominal voltage (V)", type: "text", placeholder: "230" },
        { id: "external_ze", label: "External Ze (Ω)", type: "text" },
        { id: "pfc", label: "Prospective fault current (kA)", type: "text" },
        { id: "earthing_means", label: "Means of earthing", type: "select", options: ["Distributor's facility", "Installation earth electrode"] },
        { id: "measured_ze", label: "Measured Ze (Ω)", type: "text" },
      ]},
      { title: "EV Risk Assessment", fields: [
        { id: "risk_assessment_done", label: "EV Risk assessment completed?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "risk_assessment_ref", label: "Risk assessment reference", type: "text" },
      ]},
      { title: "Schedule of Inspections", fields: [
        { id: "insp_intake", label: "1.0 Condition of consumer's intake equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_alternative", label: "2.0 Parallel or switched alternative sources", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_ads", label: "3.0 Protective measure: ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_basic", label: "4.0 Basic protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_other_prot", label: "5.0 Protective measures other than ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_additional", label: "6.0 Additional protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_distribution", label: "7.0 Distribution equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_circuits", label: "8.0 Circuits", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_isolation", label: "9.0 Isolation and switching", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_equipment", label: "10.0 Current-using equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_notices", label: "11.0 Identification and notices", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_bath", label: "12.0 Location(s) with bath or shower", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_special", label: "13.0 Other special installations", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_prosumer", label: "14.0 Prosumer's LV installation(s)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
      ]},
      { title: "Circuit Details", hasDynamicRows: true, rowFields: [
        { id: "ref", label: "Ref", type: "text" },
        { id: "designation", label: "Designation", type: "text" },
        { id: "csa_live", label: "Live (mm²)", type: "text" },
        { id: "ocpd_type", label: "OCPD type", type: "text" },
        { id: "ocpd_rating", label: "Rating (A)", type: "text" },
        { id: "rcd_type", label: "RCD type", type: "text" },
        { id: "rcd_idn", label: "IΔn (mA)", type: "text" },
        { id: "r1r2", label: "R1+R2 (Ω)", type: "text" },
        { id: "zs", label: "Zs (Ω)", type: "text" },
        { id: "ir_le", label: "IR L-E (MΩ)", type: "text" },
        { id: "polarity", label: "Polarity", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "rcd_time", label: "RCD (ms)", type: "text" },
      ]},
      { title: "Comments", fields: [
        { id: "departures", label: "Departures from BS 7671", type: "textarea", placeholder: "N/A" },
        { id: "comments", label: "Comments on existing installation", type: "textarea", placeholder: "N/A" },
        { id: "next_inspection_interval", label: "Next inspection interval", type: "text", placeholder: "e.g. 10 years" },
      ]},
    ],
  },

  "ventilation": {
    title: "Ventilation Test Certificate",
    type: "ventilation",
    accent: "gold",
    sections: [
      { title: "Test Equipment Used", fields: [
        { id: "equip_model", label: "Model", type: "text" },
        { id: "equip_type", label: "Type", type: "text" },
        { id: "equip_serial", label: "Serial number", type: "text" },
        { id: "equip_calibration_no", label: "Calibration certificate number", type: "text" },
        { id: "equip_calibration_date", label: "UKAS calibration date", type: "text" },
      ]},
      { title: "Flow Rate Testing", hasDynamicRows: true, rowFields: [
        { id: "fan_ref", label: "Fan ref" },
        { id: "manufacturer", label: "Manufacturer" },
        { id: "model", label: "Model" },
        { id: "measured_rate", label: "Measured (l/s)" },
        { id: "correction_factor", label: "Factor" },
        { id: "corrected_rate", label: "Corrected (l/s)" },
        { id: "design_rate", label: "Design (l/s)" },
        { id: "result", label: "Pass/Fail", type: "select", options: ["Pass", "Fail"] },
        { id: "reason_fail", label: "Reason if failed" },
      ]},
    ],
  },

  "electrical-risk": {
    title: "Electrical Risk Assessment",
    type: "electrical_risk",
    accent: "gold",
    sections: [
      { title: "Part 1 — Client Details", fields: [
        { id: "duty_holder", label: "Duty holder", type: "text" },
      ]},
      { title: "Part 2 — Details of Risk", fields: [
        { id: "db_location", label: "Location of distribution board", type: "text" },
        { id: "db_ref", label: "DB reference to which risk assessment applies", type: "text" },
        { id: "circuit_ref", label: "Circuit to which risk assessment applies", type: "text" },
        { id: "potential_hazards", label: "Potential hazards", type: "textarea" },
        { id: "precautions", label: "Precautions to reduce risk", type: "textarea" },
        { id: "cert_number", label: "Electrical certificate number (if applicable)", type: "text" },
        { id: "who_at_risk", label: "Who or what is at risk?", type: "textarea" },
      ]},
    ],
  },

  "electrical-isolation": {
    title: "Electrical Isolation Certificate",
    type: "electrical_isolation",
    accent: "gold",
    sections: [
      { title: "Equipment Details", fields: [
        { id: "time_isolation", label: "Time of isolation", type: "text" },
        { id: "location", label: "Location", type: "text" },
        { id: "equipment_circuit", label: "Equipment / circuit to be isolated", type: "textarea" },
        { id: "safety_locks", label: "Safety locks have been fitted at", type: "text" },
        { id: "caution_notices", label: "Caution notices posted at", type: "text" },
        { id: "fuses_removed", label: "Fuses removed", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "isolator_off", label: "Isolator in OFF position", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "mcb_off", label: "MCB in OFF position", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "safety_locks_fitted", label: "Safety locks fitted", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "tags_fitted", label: "Tags fitted", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "Notes", fields: [
        { id: "notes", label: "Notes", type: "textarea" },
      ]},
      { title: "Handover for Service", fields: [
        { id: "verified_no_voltage", label: "Isolated and verification of no voltage present", type: "select", options: ["Yes", "No"] },
        { id: "handover_name", label: "Name (competent person)", type: "text" },
      ]},
      { title: "Clearance for Service", fields: [
        { id: "clearance_name", label: "Name (competent person)", type: "text" },
        { id: "clearance_time", label: "Time", type: "text" },
      ]},
    ],
  },

  "landlords-interim": {
    title: "Landlords Interim Electrical Safety Checklist",
    type: "landlords_interim",
    accent: "gold",
    sections: [
      { title: "Conditions of Use", fields: [
        { id: "eicr_5_years", label: "Confirmation EICR carried out in the last 5 years", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "eicr_items_addressed", label: "Items recorded on EICR have been addressed", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "Fusebox / Consumer Unit", fields: [
        { id: "cu_covers", label: "1. Covers in place", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "cu_damage", label: "2. Consumer unit not cracked/damaged or overheating", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "cu_combustible", label: "3. No combustible materials stored close by", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "cu_rcd_test", label: "4. RCD/RCBO trips when test button pressed", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "cu_access", label: "5. Access to fusebox not restricted", type: "select", options: ["Pass", "Fail", "N/A"] },
      ]},
      { title: "Sockets and Lighting", fields: [
        { id: "sl_damage", label: "3.0 Sockets, switches, lights not damaged and securely fixed", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "sl_overheating", label: "3.1 Not showing signs of overheating", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "sl_overloaded", label: "3.1 Sockets not overloaded", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "sl_functional", label: "3.2 Functional check", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "sl_lamps", label: "3.3 Correct rating, wattage and type of lamps", type: "select", options: ["Pass", "Fail", "N/A"] },
      ]},
      { title: "Electrical Appliances", fields: [
        { id: "ea_recall", label: "3.5 Appliances not subject to product recall", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "ea_plug", label: "3.6 Plug not cracked, damaged or scorched", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "ea_cable", label: "3.7 Flexible cable in satisfactory condition", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "ea_cable_secure", label: "3.8 Flexible cable securely attached", type: "select", options: ["Pass", "Fail", "N/A"] },
      ]},
      { title: "Fire, Heat and CO Alarms", fields: [
        { id: "fa_clean", label: "4.1 Alarms not covered or dirty", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "fa_functional", label: "4.2 Functional check", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "fa_dates", label: "4.3 Replacement dates not exceeded", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "fa_damage", label: "4.4 Not damaged and securely fixed", type: "select", options: ["Pass", "Fail", "N/A"] },
      ]},
      { title: "General", fields: [
        { id: "gen_meter", label: "4.6 Electrical meter not damaged or tampered", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "gen_pat", label: "4.7 PAT testing certificate available (if applicable)", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "gen_emergency", label: "4.8 Emergency lighting/fire alarm records up to date", type: "select", options: ["Pass", "Fail", "N/A"] },
      ]},
      { title: "Checklist Summary", hasDynamicRows: true, rowFields: [
        { id: "risk_location", label: "Electrical Risk and Location" },
        { id: "action", label: "Action to be taken" },
      ]},
      { title: "Comments", fields: [
        { id: "comments", label: "Comments", type: "textarea" },
      ]},
    ],
  },

  "ev-risk-assessment": {
    title: "EV Charging Risk Assessment",
    type: "ev_risk_assessment",
    accent: "gold",
    sections: [
      { title: "Details of Installation", fields: [
        { id: "supply_from", label: "Supply taken from", type: "text" },
        { id: "ev_location", label: "Location of EV charging equipment", type: "text" },
        { id: "pme_tt_proposed", label: "Premises with PME supply — TT proposed for charging only?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "eic_ref", label: "Electrical installation certificate reference", type: "text" },
      ]},
      { title: "Step 1 — Identify Hazards", fields: [
        { id: "has_pme_tns", label: "(a) Does the building have a PME (TN-C-S) or public TN-S supply?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "ev_outdoor", label: "(b) Is the EV charging equipment to be installed outdoors?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "tt_for_ev", label: "(c) Is a TT system to be adopted for the EV charging equipment only?", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "Step 3 — Evaluate Risks", fields: [
        { id: "simultaneous_contact", label: "(1) Is simultaneous contact possible between PME earth and TT earth via vehicle?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "can_prevent", label: "(2) If yes, can simultaneous contact be reliably prevented?", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "Step 4 — Record Findings", fields: [
        { id: "convert_tt", label: "If precautions inadequate, less risk to convert complete installation to TT?", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "EV Checklist — Pre-installation", fields: [
        { id: "supply_adequate", label: "3.2 Existing supply adequate for additional demand", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "earthing_established", label: "3.3 Earthing arrangement established", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "earthing_bonding_compliant", label: "Earthing and bonding compliant with BS 7671", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "supply_pme_tns", label: "6.5 Is the supply TN-C-S or TN-S?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "supply_tt", label: "6.4 Is the supply TT?", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "precautions_identified", label: "6.7 Precautions necessary identified (TN-C-S/TN-S)", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "simultaneous_assessment", label: "5.1.2 Simultaneous contact assessment completed (Form B1)", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "mfr_instructions", label: "3.7 Installer reviewed manufacturer's instructions", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "planning_permission", label: "3.6 Planning permission / Building Reg approval", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "constraints_agreed", label: "3.9 Constraints discussed and agreed with client", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "repairs_agreed", label: "Necessary repairs to existing installation agreed", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "EV Checklist — Physical Installation", fields: [
        { id: "optimum_location", label: "4.2 Charging equipment in optimum location", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "impact_damage", label: "4.3 Installed to minimise vehicle impact damage", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "barriers", label: "Barriers provided if required", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "controls_height", label: "4.4 Controls between 0.75m and 1.3m above ground", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "space_doors", label: "4.5 Sufficient space to open doors and covers", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "space_ventilation", label: "4.6 Sufficient space for ventilation and cooling", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "trip_hazards", label: "4.7 Trip hazards considered and avoided", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "sockets_labelled", label: "4.9 BS 1363 sockets labelled as EV connecting points", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "EV Checklist — Completion", fields: [
        { id: "inspection_done", label: "9 Inspection carried out on disconnected installation", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "tests_pre_energize", label: "9 Non-energized tests completed prior to energizing", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "tests_post_energize", label: "9 Remaining tests completed after energizing", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "eic_completed", label: "9 EIC completed with schedule of inspections and tests", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "certs_issued", label: "9 Copy of certificates issued to person ordering work", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "defects_notified", label: "9 Customer advised of defects not rectified", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "scheme_notified", label: "9 Competent person scheme provider notified", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "demo_to_client", label: "9 Correct operation demonstrated to client", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "manual_provided", label: "9 Client provided instruction manual", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "dno_notification", label: "9 DNO notification submitted via ENA website", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
    ],
  },

  "eicr": {
    title: "Electrical Installation Condition Report",
    type: "eicr",
    accent: "gold",
    sections: [
      { title: "A — Person Ordering the Report", fields: [
        { id: "person_ordering_name", label: "Name", type: "text" },
        { id: "person_ordering_address", label: "Address", type: "textarea" },
        { id: "person_ordering_email", label: "Email", type: "text" },
      ]},
      { title: "B — Reason for Report", fields: [
        { id: "reason_for_report", label: "Reason for producing this report", type: "textarea" },
        { id: "date_inspection", label: "Date(s) inspection and testing carried out", type: "text" },
      ]},
      { title: "C — Details of the Installation", fields: [
        { id: "occupier", label: "Occupier", type: "text" },
        { id: "premises_type", label: "Description of premises", type: "select", options: ["Residential", "Commercial", "Industrial", "Other"] },
        { id: "estimated_age", label: "Estimated age of wiring system (years)", type: "text" },
        { id: "evidence_alterations", label: "Evidence of additions or alterations", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "records_available", label: "Installation records available? (Reg 651.1)", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "records_age", label: "If yes, estimated age (years)", type: "text" },
        { id: "date_last_inspection", label: "Date of last inspection", type: "text" },
        { id: "alternative_supply", label: "Alternative source of supply", type: "text", placeholder: "N/A" },
      ]},
      { title: "D — Extent and Limitations", fields: [
        { id: "extent_covered", label: "Extent of installation covered by this report", type: "textarea" },
        { id: "agreed_limitations", label: "Agreed limitations including reasons (Reg 653.2)", type: "textarea", placeholder: "N/A" },
        { id: "limitations_agreed_with", label: "Limitations agreed with", type: "text" },
        { id: "operational_limitations", label: "Operational limitations including reasons", type: "textarea" },
      ]},
      { title: "E — Summary of Condition", fields: [
        { id: "general_condition", label: "General condition of installation (electrical safety)", type: "textarea" },
        { id: "overall_assessment", label: "Overall assessment — suitability for continued use", type: "select", options: ["Satisfactory", "Unsatisfactory"] },
      ]},
      { title: "F — Next Inspection", fields: [
        { id: "next_inspection_date", label: "Recommended further inspection before (date)", type: "text" },
        { id: "next_inspection_reason", label: "Reason for recommendation", type: "textarea" },
      ]},
      { title: "I — Supply Characteristics", fields: [
        { id: "supply_system", label: "Earthing system", type: "select", options: ["TN-S", "TN-C-S", "TN-C-S (PNB)", "TT", "IT", "TN-C"] },
        { id: "supply_conductors", label: "Live conductors", type: "select", options: ["1-phase (2 wire)", "2-phase (3 wire)", "3-phase (3 wire)", "3-phase (4 wire)"] },
        { id: "supply_type", label: "Supply type", type: "select", options: ["AC", "DC"] },
        { id: "nominal_voltage", label: "Nominal voltage (V)", type: "text", placeholder: "230" },
        { id: "nominal_frequency", label: "Nominal frequency (Hz)", type: "text", placeholder: "50" },
        { id: "pfc", label: "Prospective fault current Ipf (kA)", type: "text" },
        { id: "external_ze", label: "External Ze (Ω)", type: "text" },
        { id: "supply_polarity", label: "Confirmation of supply polarity", type: "select", options: ["Confirmed", "Not confirmed"] },
      ]},
      { title: "Earthing & Bonding", fields: [
        { id: "earthing_material", label: "Earthing conductor material", type: "select", options: ["Copper", "Aluminium", "Steel"] },
        { id: "earthing_csa", label: "Earthing conductor csa (mm²)", type: "text" },
        { id: "earthing_continuity", label: "Earthing continuity verified", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_material", label: "Main bonding conductor material", type: "select", options: ["Copper", "Aluminium", "Steel"] },
        { id: "bonding_csa", label: "Main bonding conductor csa (mm²)", type: "text" },
        { id: "bonding_water", label: "Water installation pipes", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_gas", label: "Gas installation pipes", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_oil", label: "Oil installation pipes", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_structural", label: "Structural steel", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_lightning", label: "Lightning protection", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_other", label: "Other", type: "text" },
      ]},
      { title: "Main Switch / Circuit-Breaker / RCD", fields: [
        { id: "main_switch_type", label: "Type BS(EN)", type: "text" },
        { id: "main_switch_poles", label: "No. of poles", type: "text" },
        { id: "main_switch_voltage", label: "Voltage rating (V)", type: "text", placeholder: "230" },
        { id: "main_switch_current", label: "Current rating (A)", type: "text" },
        { id: "main_rcd_current", label: "RCD operating current (mA)", type: "text", placeholder: "N/A" },
        { id: "main_rcd_time", label: "RCD operating time (ms)", type: "text", placeholder: "N/A" },
        { id: "main_rcd_type", label: "RCD type", type: "text", placeholder: "N/A" },
        { id: "supply_conductor_material", label: "Supply conductor material", type: "select", options: ["Copper", "Aluminium"] },
        { id: "supply_conductor_csa", label: "Supply conductor csa (mm²)", type: "text" },
      ]},
      { title: "Schedule of Inspections — 1.0 Consumer's Intake Equipment", fields: [
        { id: "insp_1_1", label: "1.1 Service cable adequacy", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_1_2", label: "1.2 Service head condition", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_1_3", label: "1.3 Meter equipment condition (visual)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_1_4", label: "1.4 Meter tails — condition and adequacy", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_1_5", label: "1.5 Isolator (main switch) condition & operation", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 2.0 Parallel / Alternative Sources", fields: [
        { id: "insp_2_1", label: "2.1 Adequacy of means of isolating alternative source", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_2_2", label: "2.2 Condition of alternative source equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_2_3", label: "2.3 Correct operation of changeover or interlocking", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 3.0 ADS (Protective Measure)", fields: [
        { id: "insp_3_1", label: "3.1 Presence of earthing conductor", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_2", label: "3.2 Earthing conductor adequacy (16mm² min for TN)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_3", label: "3.3 Connection of earthing conductor at MET", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_4", label: "3.4 Earthing conductor accessible and correctly labelled", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_5", label: "3.5 Main bonding conductors — presence", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_6", label: "3.6 Main bonding conductors — adequacy and condition", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_7", label: "3.7 Main bonding conductor connections", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_8", label: "3.8 Supplementary bonding conductors — presence", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_9", label: "3.9 Supplementary bonding conductors — adequacy", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_3_10", label: "3.10 CPCs presence and adequacy", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 4.0 Basic Protection", fields: [
        { id: "insp_4_1", label: "4.1 Insulation of live parts adequacy", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_4_2", label: "4.2 Barriers or enclosures — presence and adequacy", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_4_3", label: "4.3 Protection against direct contact — finger/object test", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 5.0 to 6.0", fields: [
        { id: "insp_5_1", label: "5.1 SELV / PELV / Electrical separation — presence", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_5_2", label: "5.2 Non-conducting locations", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_6_1", label: "6.1 RCDs — presence for additional protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_6_2", label: "6.2 RCD rated ≤ 30mA for all socket-outlets ≤ 32A", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_6_3", label: "6.3 RCD rated ≤ 30mA for mobile equipment outdoors ≤ 32A", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_6_4", label: "6.4 Supplementary bonding where RCD not provided", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 7.0 Distribution Equipment", fields: [
        { id: "insp_7_1", label: "7.1 Consumer unit / DB condition and suitability", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_2", label: "7.2 Consumer unit / DB enclosure (non-combustible per 421.1.201)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_3", label: "7.3 Security of fixings", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_4", label: "7.4 All connections secure / no signs of overheating", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_5", label: "7.5 Single-pole switching in line conductor only", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_6", label: "7.6 Correct type and rating of protective devices", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_7", label: "7.7 Enclosure not damaged — entry holes sealed", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_7_8", label: "7.8 Presence of SPD (Surge Protection Device)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 8.0 Circuits", fields: [
        { id: "insp_8_1", label: "8.1 Identification of cables — correct identification", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_2", label: "8.2 Cable installation and routing adequate", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_3", label: "8.3 Selection of cable for current carrying capacity", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_4", label: "8.4 Cables concealed in prescribed zones or mechanical protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_5", label: "8.5 No cables in thermal insulation", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_6", label: "8.6 Cable condition — not damaged", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_7", label: "8.7 Junction boxes/connections accessible", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_8_8", label: "8.8 Socket-outlets and accessories condition", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "Schedule of Inspections — 9.0 to 14.0", fields: [
        { id: "insp_9_1", label: "9.1 Means of isolation provided", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_9_2", label: "9.2 Main switch accessible and functioning", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_9_3", label: "9.3 Functional switching provided", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_10_1", label: "10.1 Suitability of equipment for environment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_10_2", label: "10.2 Equipment installed per manufacturers instructions", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_11_1", label: "11.1 Warning notices / labels / circuit chart present", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_11_2", label: "11.2 Circuit identification at distribution board", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_12_1", label: "12.1 Bath/shower zones — requirements met", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_12_2", label: "12.2 IP rating of equipment in zones", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_12_3", label: "12.3 Supplementary bonding in bathroom (if required)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_13_1", label: "13.1 Other special installations or locations present", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
        { id: "insp_14_1", label: "14.1 Prosumer's LV installation requirements", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3", "LIM"] },
      ]},
      { title: "K — Observations", hasDynamicRows: true, rowFields: [
        { id: "item_no", label: "Item No." },
        { id: "observation", label: "Observation" },
        { id: "code", label: "Code", type: "select", options: ["C1", "C2", "C3", "FI"] },
      ]},
      { title: "Distribution Board Details", fields: [
        { id: "db_ref", label: "DB ref", type: "text", placeholder: "DB1" },
        { id: "db_location", label: "DB location", type: "text" },
        { id: "db_ipf", label: "Ipf at board (kA)", type: "text" },
        { id: "db_switch_type", label: "Main switch type BS(EN)", type: "text" },
        { id: "db_supplied_from", label: "Supplied from", type: "text" },
        { id: "db_rating", label: "Rating (A)", type: "text" },
        { id: "db_spd", label: "SPD type / status", type: "text", placeholder: "N/A" },
        { id: "db_supply_mm", label: "Supply (mm²)", type: "text" },
        { id: "db_earth_mm", label: "Earth (mm²)", type: "text" },
        { id: "db_phases", label: "No. of phases", type: "text" },
        { id: "db_supply_device", label: "Supply protective device BS(EN) ref", type: "text" },
        { id: "db_supply_device_rating", label: "Supply device rating (A)", type: "text" },
      ]},
      { title: "Circuit Details & Test Results", hasDynamicRows: true, rowFields: [
        { id: "ref", label: "Ref" },
        { id: "designation", label: "Designation" },
        { id: "csa_live", label: "Live mm²" },
        { id: "csa_cpc", label: "CPC mm²" },
        { id: "ocpd_type", label: "OCPD type" },
        { id: "ocpd_rating", label: "Rating A" },
        { id: "rcd_type", label: "RCD type" },
        { id: "rcd_idn", label: "IΔn mA" },
        { id: "r1r2", label: "R1+R2 Ω" },
        { id: "zs", label: "Zs Ω" },
        { id: "ir_le", label: "IR L-E MΩ" },
        { id: "polarity", label: "Pol", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "rcd_time", label: "RCD ms" },
      ]},
    ],
  },

  "domestic-eic": {
    title: "Domestic Electrical Installation Certificate",
    type: "domestic_eic",
    accent: "gold",
    sections: [
      { title: "Details of the Installation", fields: [
        { id: "installation_description", label: "Description of installation", type: "textarea" },
        { id: "installation_extent", label: "Extent of installation covered", type: "textarea" },
        { id: "installation_type", label: "Installation type", type: "select", options: ["New installation", "Addition to existing", "Alteration to existing"] },
      ]},
      { title: "Design, Construction & Inspection", fields: [
        { id: "departures", label: "Details of departures from BS 7671", type: "textarea", placeholder: "None" },
        { id: "designer_name", label: "Designer name", type: "text" },
        { id: "constructor_name", label: "Constructor name", type: "text" },
        { id: "inspector_name", label: "Inspector name", type: "text" },
      ]},
      { title: "Supply Characteristics & Earthing", fields: [
        { id: "supply_system", label: "Earthing system", type: "select", options: ["TN-S", "TN-C-S", "TN-C-S (PNB)", "TT", "IT"] },
        { id: "supply_conductors", label: "Live conductors", type: "select", options: ["1-phase (2 wire)", "3-phase (4 wire)"] },
        { id: "nominal_voltage", label: "Nominal voltage (V)", type: "text", placeholder: "230" },
        { id: "pfc", label: "Prospective fault current (kA)", type: "text" },
        { id: "external_ze", label: "External Ze (Ω)", type: "text" },
        { id: "supply_polarity", label: "Supply polarity confirmed", type: "select", options: ["Confirmed", "Not confirmed"] },
        { id: "overcurrent_type", label: "Supply overcurrent device type", type: "text" },
        { id: "overcurrent_rated", label: "Rated current (A)", type: "text" },
      ]},
      { title: "Installation at Origin", fields: [
        { id: "earthing_means", label: "Means of earthing", type: "select", options: ["Distributor's facility", "Installation earth electrode"] },
        { id: "electrode_ra", label: "Electrode RA (Ω)", type: "text", placeholder: "N/A" },
        { id: "max_demand", label: "Maximum demand (A)", type: "text" },
        { id: "main_switch_type", label: "Main switch type", type: "text" },
        { id: "main_switch_current", label: "Current rating (A)", type: "text" },
        { id: "main_switch_poles", label: "No. of poles", type: "text" },
        { id: "supply_conductor_csa", label: "Supply conductor csa (mm²)", type: "text" },
        { id: "earthing_conductor_csa", label: "Earthing conductor csa (mm²)", type: "text" },
        { id: "bonding_conductor_csa", label: "Main bonding conductor csa (mm²)", type: "text" },
        { id: "bonding_water", label: "Water pipes bonded", type: "select", options: ["Yes", "No", "N/A"] },
        { id: "bonding_gas", label: "Gas pipes bonded", type: "select", options: ["Yes", "No", "N/A"] },
        { id: "bonding_oil", label: "Oil pipes bonded", type: "select", options: ["Yes", "No", "N/A"] },
      ]},
      { title: "Schedule of Inspections", fields: [
        { id: "insp_1", label: "1.0 Condition of consumer's intake equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_2", label: "2.0 Parallel or switched alternative sources", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_3", label: "3.0 Protective measure: ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_4", label: "4.0 Basic protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_5", label: "5.0 Protective measures other than ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_6", label: "6.0 Additional protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_7", label: "7.0 Distribution equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_8", label: "8.0 Circuits", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_9", label: "9.0 Isolation and switching", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_10", label: "10.0 Current-using equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_11", label: "11.0 Identification and notices", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_12", label: "12.0 Location(s) with bath or shower", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
      ]},
      { title: "Distribution Board", fields: [
        { id: "db_ref", label: "DB ref", type: "text", placeholder: "DB1" },
        { id: "db_location", label: "DB location", type: "text" },
        { id: "db_ipf", label: "Ipf at board (kA)", type: "text" },
        { id: "db_switch_type", label: "Main switch type", type: "text" },
        { id: "db_rating", label: "Rating (A)", type: "text" },
        { id: "db_spd", label: "SPD type / status", type: "text", placeholder: "N/A" },
      ]},
      { title: "Circuit Details & Test Results", hasDynamicRows: true, rowFields: [
        { id: "ref", label: "Ref" },
        { id: "designation", label: "Designation" },
        { id: "csa_live", label: "Live mm²" },
        { id: "csa_cpc", label: "CPC mm²" },
        { id: "ocpd_type", label: "OCPD type" },
        { id: "ocpd_rating", label: "Rating A" },
        { id: "rcd_type", label: "RCD type" },
        { id: "rcd_idn", label: "IΔn mA" },
        { id: "r1r2", label: "R1+R2 Ω" },
        { id: "zs", label: "Zs Ω" },
        { id: "ir_le", label: "IR L-E MΩ" },
        { id: "polarity", label: "Pol", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "rcd_time", label: "RCD ms" },
      ]},
      { title: "Comments & Next Inspection", fields: [
        { id: "comments", label: "Comments on existing installation", type: "textarea", placeholder: "N/A" },
        { id: "next_inspection_interval", label: "Recommended next inspection interval", type: "text" },
      ]},
    ],
  },

  "eic": {
    title: "Electrical Installation Certificate",
    type: "eic",
    accent: "gold",
    sections: [
      { title: "Details of the Installation", fields: [
        { id: "installation_description", label: "Description of installation", type: "textarea" },
        { id: "installation_extent", label: "Extent of installation covered by this certificate", type: "textarea" },
        { id: "installation_type", label: "Installation type", type: "select", options: ["New installation", "Addition to existing", "Alteration to existing"] },
      ]},
      { title: "Design", fields: [
        { id: "design_departures", label: "Details of departures from BS 7671", type: "textarea", placeholder: "N/A" },
        { id: "risk_assessment_attached", label: "Risk assessment attached", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "designer_name", label: "Designer name", type: "text" },
        { id: "designer_2_name", label: "Designer 2 name (if applicable)", type: "text", placeholder: "N/A" },
      ]},
      { title: "Construction", fields: [
        { id: "construction_departures", label: "Details of departures from BS 7671", type: "textarea", placeholder: "N/A" },
        { id: "constructor_name", label: "Constructor name", type: "text" },
      ]},
      { title: "Inspection & Testing", fields: [
        { id: "testing_departures", label: "Details of departures from BS 7671", type: "textarea", placeholder: "N/A" },
        { id: "inspector_name", label: "Inspector name", type: "text" },
        { id: "reviewer_name", label: "Qualified supervisor / reviewer name", type: "text", placeholder: "N/A" },
      ]},
      { title: "Supply Characteristics & Earthing", fields: [
        { id: "supply_system", label: "Earthing system", type: "select", options: ["TN-S", "TN-C-S", "TN-C-S (PNB)", "TT", "IT", "TN-C"] },
        { id: "supply_conductors", label: "Live conductors", type: "select", options: ["1-phase (2 wire)", "2-phase (3 wire)", "3-phase (3 wire)", "3-phase (4 wire)"] },
        { id: "nominal_voltage", label: "Nominal voltage (V)", type: "text", placeholder: "400/230" },
        { id: "nominal_frequency", label: "Nominal frequency (Hz)", type: "text", placeholder: "50" },
        { id: "pfc", label: "Prospective fault current (kA)", type: "text" },
        { id: "external_ze", label: "External Ze (Ω)", type: "text" },
        { id: "supply_polarity", label: "Supply polarity confirmed", type: "select", options: ["Confirmed", "Not confirmed"] },
        { id: "num_supplies", label: "Number of supplies", type: "text", placeholder: "1" },
        { id: "overcurrent_type", label: "Supply overcurrent device type BS/EN", type: "text" },
        { id: "overcurrent_rated", label: "Rated current (A)", type: "text" },
        { id: "overcurrent_breaking", label: "Breaking capacity (kA)", type: "text" },
      ]},
      { title: "Installation at Origin", fields: [
        { id: "earthing_means", label: "Means of earthing", type: "select", options: ["Distributor's facility", "Installation earth electrode"] },
        { id: "electrode_type", label: "Electrode type (if applicable)", type: "text", placeholder: "N/A" },
        { id: "electrode_ra", label: "Electrode RA (Ω)", type: "text", placeholder: "N/A" },
        { id: "max_demand", label: "Maximum demand (A)", type: "text" },
        { id: "main_switch_type", label: "Main switch type BS(EN)", type: "text" },
        { id: "main_switch_poles", label: "No. of poles", type: "text" },
        { id: "main_switch_voltage", label: "Voltage rating (V)", type: "text", placeholder: "230" },
        { id: "main_switch_current", label: "Current rating (A)", type: "text" },
        { id: "supply_conductor_material", label: "Supply conductor material", type: "select", options: ["Copper", "Aluminium"] },
        { id: "supply_conductor_csa", label: "Supply conductor csa (mm²)", type: "text" },
        { id: "earthing_conductor_material", label: "Earthing conductor material", type: "select", options: ["Copper", "Aluminium"] },
        { id: "earthing_conductor_csa", label: "Earthing conductor csa (mm²)", type: "text" },
        { id: "bonding_conductor_material", label: "Main bonding conductor material", type: "select", options: ["Copper", "Aluminium"] },
        { id: "bonding_conductor_csa", label: "Main bonding conductor csa (mm²)", type: "text" },
        { id: "bonding_water", label: "Water installation pipes", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_gas", label: "Gas installation pipes", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_oil", label: "Oil installation pipes", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_structural", label: "Structural steel", type: "select", options: ["N/A", "Yes", "No"] },
        { id: "bonding_lightning", label: "Lightning protection", type: "select", options: ["N/A", "Yes", "No"] },
      ]},
      { title: "Schedule of Inspections", fields: [
        { id: "insp_1", label: "1.0 Condition of consumer's intake equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_2", label: "2.0 Parallel or switched alternative sources", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_3", label: "3.0 Protective measure: ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_4", label: "4.0 Basic protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_5", label: "5.0 Protective measures other than ADS", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_6", label: "6.0 Additional protection", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_7", label: "7.0 Distribution equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_8", label: "8.0 Circuits", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_9", label: "9.0 Isolation and switching", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_10", label: "10.0 Current-using equipment", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_11", label: "11.0 Identification and notices", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_12", label: "12.0 Location(s) with bath or shower", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_13", label: "13.0 Other special installations", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
        { id: "insp_14", label: "14.0 Prosumer's LV installation(s)", type: "select", options: ["Pass", "N/A", "C1", "C2", "C3"] },
      ]},
      { title: "Comments & Next Inspection", fields: [
        { id: "comments", label: "Comments on existing installation (Reg 644.1.2)", type: "textarea", placeholder: "N/A" },
        { id: "next_inspection_interval", label: "Recommended next inspection interval", type: "text" },
      ]},
      { title: "Distribution Board Details", fields: [
        { id: "db_ref", label: "DB ref", type: "text", placeholder: "DB1" },
        { id: "db_location", label: "DB location", type: "text" },
        { id: "db_ipf", label: "Ipf at board (kA)", type: "text" },
        { id: "db_switch_type", label: "Main switch type BS(EN)", type: "text" },
        { id: "db_supplied_from", label: "Supplied from", type: "text" },
        { id: "db_rating", label: "Rating (A)", type: "text" },
        { id: "db_spd", label: "SPD type / status", type: "text", placeholder: "N/A" },
        { id: "db_supply_mm", label: "Supply (mm²)", type: "text" },
        { id: "db_earth_mm", label: "Earth (mm²)", type: "text" },
        { id: "db_phases", label: "No. of phases", type: "text" },
      ]},
      { title: "Circuit Details & Test Results", hasDynamicRows: true, rowFields: [
        { id: "ref", label: "Ref" },
        { id: "designation", label: "Designation" },
        { id: "csa_live", label: "Live mm²" },
        { id: "csa_cpc", label: "CPC mm²" },
        { id: "ocpd_type", label: "OCPD type" },
        { id: "ocpd_rating", label: "Rating A" },
        { id: "rcd_type", label: "RCD type" },
        { id: "rcd_idn", label: "IΔn mA" },
        { id: "r1r2", label: "R1+R2 Ω" },
        { id: "zs", label: "Zs Ω" },
        { id: "ir_le", label: "IR L-E MΩ" },
        { id: "polarity", label: "Pol", type: "select", options: ["Pass", "Fail", "N/A"] },
        { id: "rcd_time", label: "RCD ms" },
      ]},
    ],
  },
};
